import React from 'react';
import { ArrowUpIcon } from '@heroicons/react/24/outline';
import { useShellInsets, SCROLL_TOP_BUTTON_GUTTER_PX } from '@/hooks/common';
import { useToastStore } from '@/stores';

type ScrollToTopButtonProps = {
  show: boolean;
  onClick: () => void;
};

/**
 * The button's corner spacing, used for both its right inset and its bottom. Lives
 * with the shell insets because the floating bars reserve a lane from it
 * (SCROLL_TOP_BUTTON_LANE_PX) — the two must not drift apart.
 */
const CORNER_GUTTER_PX = SCROLL_TOP_BUTTON_GUTTER_PX;

/**
 * Where the button parks while a toast is up. The toast column is fixed in the
 * same corner (`bottom-4`, right = rail + gutter) at the same z-50, and Layout
 * renders it after <main>, so it paints over the button: at the resting offset a
 * single toast covers 30 of the button's 40px and elementFromPoint at its centre
 * returns the toast, exactly like the filter rail did. Raising the z-index would
 * only swap which control is blocked — the toast's dismiss ✕ falls inside that
 * same overlap. 88px clears the column's own 16px offset plus a one- or two-line
 * toast; a deeper stack still reaches the button, and that is accepted — toasts
 * auto-dismiss in 3s.
 */
const TOAST_CLEARANCE_PX = 88;

export const ScrollToTopButton: React.FC<ScrollToTopButtonProps> = ({ show, onClick }) => {
  // LR-600: a fixed element ignores <main>'s pr-*, so the old `right-8` put the
  // button 32px off the VIEWPORT edge — i.e. entirely beneath the filter rail
  // (72px collapsed, 288px open) which paints over it at z-9998 vs this z-50.
  // Anchoring to the shell inset keeps it beside the rail instead of under it.
  const insets = useShellInsets(CORNER_GUTTER_PX);
  const hasToast = useToastStore((s) => s.toasts.length > 0);

  if (!show) return null;

  return (
    <button
      onClick={onClick}
      // transition-all animates both the rail-driven `right` (in step with the
      // rail's own 300ms) and the toast-driven `bottom`.
      className="fixed p-2.5 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 z-50 hover:scale-110"
      style={{
        right: insets.right,
        bottom: hasToast ? TOAST_CLEARANCE_PX : CORNER_GUTTER_PX,
        animation: 'slideInUp 0.3s ease-out',
      }}
      title="Scroll to top"
      aria-label="Scroll to top"
    >
      <ArrowUpIcon className="h-5 w-5" />
    </button>
  );
};
